import { useEffect, useMemo, useRef } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs, InlineEditConflictArgs } from "@/components/table/use-inline-edits";
import { ApiError, apiFetch } from "@/lib/api";
import type { AlertRuleResource, Page } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { CADENCE_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import {
  conflictMessage,
  freshestKnownValue,
  type EditCommitOutcome,
  type EditCommitPayload,
} from "@/lib/inline-edit";

/**
 * "backup-missing" -> "Backup missing". No per-rule label table (spec
 * Boundaries: "no second, UI-owned copy of a rule's declared metadata") —
 * this is a mechanical transform of the rule's own `rule_key`, not a
 * hand-maintained name that could drift or need updating as new rules are
 * registered in future epics.
 */
export function humanizeRuleKey(key: string): string {
  const words = key.split("-").filter((word) => word.length > 0);
  if (words.length === 0) {
    return key;
  }
  const [first, ...rest] = words;
  return [(first ?? "").charAt(0).toUpperCase() + (first ?? "").slice(1), ...rest].join(" ");
}

/** `AlertRuleResource.threshold` is always either `""` (no threshold) or a
 * JSON-text encoding of the real value (`app/api/alert_rules.py`'s own
 * `_render_threshold`) — displayed as-is, an em dash standing in for empty
 * the same way `DataTable.renderCell`'s own default does for any other
 * empty field. */
function formatThreshold(raw: string): string {
  return raw === "" ? "—" : raw;
}

function formatCeilingExempt(exempt: boolean): string {
  return exempt ? "Yes" : "No";
}

/** Mirrors `UsersList.tsx`'s identical helper: the value half of the PATCH
 * body a commit produces. Only `threshold` is ever inline-edited on this
 * screen (spec judgement call 2), so this always receives a `"value"`-kind
 * payload. */
function patchBodyFor(columnKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for column "${columnKey}"`);
  }
  return { [columnKey]: payload.value };
}

/** The conflict banner shows the raw JSON-text value directly — there is no
 * per-rule label map for a threshold's own shape (the same reason
 * `formatThreshold` above does not try to prettify it further). */
function labelForConflictValue(_field: string, rawValue: string): string {
  return formatThreshold(rawValue);
}

/**
 * Settings → Alerts (Story 3.8): every registered rule's threshold, cadence,
 * terminal condition and ceiling exemption in one place — the shared table
 * (Epic 2) in inline-edit mode, wired to `GET /alert-rules` and
 * `PATCH /alert-rules/{id}`. Visible to every signed-in account
 * (`GET /alert-rules` is `role=Role.READ_ONLY`); only an Owner sees the
 * threshold cell as editable (`useIsOwner`, not `useIsReadOnly` — the PATCH
 * route is Owner-only, not Member-editable, spec judgement call 4), and the
 * affordance is absent, never disabled, for anyone else.
 *
 * No per-rule record page: there is nothing beyond what this one row
 * already shows (spec's own Code Map names no record page), so the
 * identifier column's required `getRowHref` points back at this same
 * screen — a click on it is inert, not a navigation to a page that does not
 * exist.
 */
export function AlertsList() {
  const queryClient = useQueryClient();
  const isOwner = useIsOwner();
  /** Same "second edit before the first has re-rendered" guard
   * `UsersList.tsx` carries (review finding 3 there) — kept here for the
   * identical reason, even though a family reasonably edits this screen far
   * less often than the user list. */
  const latestUpdatedAtRef = useRef<Record<string, string>>({});

  useEffect(() => {
    document.title = "Alerts — PropDesk";
  }, []);

  const listQueryKey = ["alert-rules", "list"] as const;
  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<AlertRuleResource>>("/alert-rules"),
    placeholderData: keepPreviousData,
  });

  function writeRowToCache(updated: AlertRuleResource) {
    latestUpdatedAtRef.current[updated.id] = updated.updated_at;
    queryClient.setQueriesData<Page<AlertRuleResource>>({ queryKey: ["alert-rules"] }, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      };
    });
  }

  async function sendPatch(
    row: AlertRuleResource,
    column: DataTableColumn<AlertRuleResource>,
    payload: EditCommitPayload,
    expectedUpdatedAt: string,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<AlertRuleResource>(`/alert-rules/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...patchBodyFor(column.key, payload),
          expected_updated_at: expectedUpdatedAt,
        }),
      });
      writeRowToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError && error.code === "conflict") {
        const message = conflictMessage(
          {
            field: error.field,
            currentValue: error.currentValue,
            attemptedValue: payload.kind === "value" ? payload.value : undefined,
            fallbackMessage: error.message,
          },
          labelForConflictValue,
        );
        return {
          ok: false,
          conflict: true,
          message,
          ...(error.currentValue !== undefined ? { currentValue: error.currentValue } : {}),
        };
      }
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<AlertRuleResource>): Promise<EditCommitOutcome> {
    const expectedUpdatedAt = freshestKnownValue(latestUpdatedAtRef.current, args.row.id, args.row.updated_at);
    return sendPatch(args.row, args.column, args.payload, expectedUpdatedAt);
  }

  async function resolveConflictEdit(
    args: InlineEditConflictArgs<AlertRuleResource>,
  ): Promise<EditCommitOutcome> {
    const fresh = await queryClient.fetchQuery({
      queryKey: listQueryKey,
      queryFn: () => apiFetch<Page<AlertRuleResource>>("/alert-rules"),
    });
    const freshRow = fresh.data.find((candidate) => candidate.id === args.row.id);
    if (freshRow !== undefined) {
      latestUpdatedAtRef.current[freshRow.id] = freshRow.updated_at;
    }
    if (args.resolution === "theirs") {
      return { ok: true };
    }
    if (freshRow === undefined) {
      return { ok: false, message: "That alert rule could not be found. It may have already been removed." };
    }
    return sendPatch(freshRow, args.column, { kind: "value", value: args.myValue }, freshRow.updated_at);
  }

  const columns = useMemo<DataTableColumn<AlertRuleResource>[]>(() => {
    const definitions: DataTableColumn<AlertRuleResource>[] = [
      { key: "rule_key", sortable: false, render: (row) => humanizeRuleKey(row.rule_key) },
      { key: "threshold", sortable: false, render: (row) => formatThreshold(row.threshold) },
      {
        key: "cadence",
        sortable: false,
        render: (row) => CADENCE_LABELS[row.cadence] ?? row.cadence,
      },
      { key: "terminal_condition", sortable: false },
      {
        key: "ceiling_exempt",
        sortable: false,
        render: (row) => formatCeilingExempt(row.ceiling_exempt),
      },
    ];
    // Owner-only editing (spec judgement call 4): the affordance is absent
    // for a Member or a Read-only account, never rendered disabled — the
    // same "attach `edit` conditionally" technique `UsersList.tsx` uses for
    // its own role column, applied here to withhold from anyone who is not
    // specifically confirmed to be an Owner (`useIsOwner`, not
    // `useIsReadOnly` — a Member is not read-only, but this route refuses
    // a Member too).
    if (isOwner) {
      definitions[1] = {
        key: "threshold",
        sortable: false,
        render: (row) => formatThreshold(row.threshold),
        edit: { kind: "text" },
      };
    }
    return definitions;
  }, [isOwner]);

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Alerts</h1>
      <p className="mb-6 mt-2 text-body text-ink-secondary">
        Every alert rule PropDesk can raise, and the threshold each one fires at. Changing "
        {labelFor("threshold")}" takes effect right away.
      </p>
      <DataTable
        caption="Alerts"
        itemNamePlural="alert rules"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        getRowHref={() => "/settings/alerts"}
        emptyState={{
          heading: "No alert rules are registered.",
          body: "Every deployment registers its rules at startup, so this state does not occur in practice.",
        }}
        onCommitEdit={commitEdit}
        onResolveConflict={resolveConflictEdit}
      />
    </div>
  );
}
